import { IComparable } from "../Utils/ObjUtils";
import { ByteArrayUtils } from "../Utils/ByteArrayUtils";
import { IpVersion, IpAddress } from "./IpAddress";
import { IpNetMask } from "./IpNetMask";
import { HashSet } from "../Utils/HashSet";

export class IpSubnet implements IComparable{

	private _address: IpAddress;
	public get Address(): IpAddress {
		return this._address;
	}

	private _mask: IpNetMask;
	public get Mask(): IpNetMask {
		return this._mask;
	}

	private _netAddress: IpAddress;
	public get NetAddress(): IpAddress {
		return this._netAddress;
	}

	private _minAddress: IpAddress;
	public get MinAddress() : IpAddress {
		return this._minAddress;
	}

	private _maxAddress: IpAddress;
	public get MaxAddress(): IpAddress {
		return this._maxAddress;
	}

	public static Create(addr: IpAddress, netmask: IpNetMask): IpSubnet {
		if (addr == null || netmask == null)
			return null;

		if (addr.IpVersion != netmask.IpVersion)
			return null;

		return new IpSubnet(addr, netmask);
	}

	public static CreateFromAddresses(addr: IpAddress, netmask: IpAddress): IpSubnet {
	
		if (!addr || !netmask)
			return null;

		if (addr.IpVersion != netmask.IpVersion)
			return null;

		var ipNetMask = IpNetMask.Create(netmask);
		if (!ipNetMask)
			return null;

		var networkAddress = ipNetMask.ApplyMask(addr);

		return new IpSubnet(networkAddress, ipNetMask);
	}

	public static CreateFromString(subnetStr: string): IpSubnet {
	
		if (!subnetStr)
			return null;

		var slashIdx = subnetStr.indexOf('/');
		if (slashIdx < 0)
			return null;

		var addressStr = subnetStr.substring(0, slashIdx).trim();
		if (!addressStr)
			return null;

		var maskStr = subnetStr.substring(slashIdx + 1).trim();
		if (!maskStr)
			return null;

		var address = IpAddress.Create(addressStr);
		if (address == null)
			return null;

		var mask = null;

		var maskLength = +maskStr;
		if (maskLength > 0) {
			mask = IpNetMask.CreateFromVersion(address.IpVersion, maskLength);
		}
		else {
			var maskAddress = IpAddress.Create(maskStr);
			if (maskAddress == null)
				return null;

			mask = IpNetMask.Create(maskAddress);
		}

		return IpSubnet.Create(address, mask);
	}

	constructor(addr:IpAddress, mask: IpNetMask) {
		this._address = addr;
		this._mask = mask;
		this._netAddress = mask.ApplyMask(addr);
		this._minAddress = this.NetAddress.Clone();
		this._maxAddress = this.NetAddress.Clone();
		ByteArrayUtils.SetLeastSignificantBits(this.MaxAddress.Bytes, this.Mask.HostLength);
	}

	public Clone(): IpSubnet {
		var clonedSubnet = new IpSubnet(this.Address.Clone(),this.Mask.Clone());
		return clonedSubnet;
	}

	public Contains(ip: IpAddress): boolean {
		if (!ip)
			return false;
		if (ip.IpVersion == IpVersion.IPv6)
			return false;

		var maskedIp = this.Mask.ApplyMask(ip);
		return maskedIp.Equals(this.NetAddress);
	}

	public toString(): string {
		return this.Address.toString() + "/" + this.Mask.toString();
	}

	public ToString(): string {
		return this.toString();
	}

	public Equals(obj: any) : boolean	{
		if (!obj)
			return false;
		if (!(obj instanceof IpSubnet))
			return false;

		var other = <IpSubnet>obj;
		return (this.Address.Equals(other.Address) && this.Mask.Equals(other.Mask));
	}

	public CompareTo(other: IpSubnet) : number{
		if (other == null)
			return 1;
		else {
			if ((this.Address == null) && (other.Address == null))
				return 0;
			else if ((this.Address == null) && (other.Address != null))
				return -1;
			else {
				var compare = this.Address.CompareTo(other.Address);
				if (compare == 0) {
					var maxIp = this.MaxAddress;
					var otherMaxIp = other.MaxAddress;

					if ((maxIp == null) && (otherMaxIp == null))
						compare = 0;
					else if ((maxIp == null) && (otherMaxIp != null))
						compare = -1;
					else
						compare = maxIp.CompareTo(otherMaxIp);
				}

				return compare;
			}
		}
	}

	public GetHashCode() : number {
		return (this.Address.GetHashCode() ^ this.Mask.GetHashCode());
	}

	public get IpAddresses(): HashSet<IpAddress> {
		var ipAddresses = new HashSet<IpAddress>();

		var minIp = this.MinAddress;
		var maxIp = this.MaxAddress;

		if ((minIp != null) && (maxIp != null)) {
			var nextIp = minIp;

			while (nextIp.CompareTo(maxIp) <= 0) {
				ipAddresses.Add(nextIp.Clone());
				nextIp.Increment();
			}
		}

		return (ipAddresses);
	}

	public get ValidDiscoveryIpAddresses(): HashSet<IpAddress> {
		var ipAddresses = new HashSet<IpAddress>();

		var minIp = this.MinAddress.Clone();
		var maxIp = this.MaxAddress.Clone();

		if ((minIp != null) && (maxIp != null)) {
			minIp.Increment(); // The NETWORK address is NOT a valid IP for DISCOVERY
			maxIp.Decrement(); // The BROADCAST address is NOT a VALID IP for DISCOVERY

			var nextIp = minIp;

			while (nextIp.CompareTo(maxIp) <= 0) {
				ipAddresses.Add(nextIp.Clone());
				nextIp.Increment();
			}
		}

		return (ipAddresses);
	}
}
